import { useSyncExternalStore } from 'react'
import { readPhase, readT, readScene, dayAt, skyFor, choosePhase, chooseScene, scatterClouds, applySky, nextPhase, SCENES } from '../lib/sky.mjs'
import { track } from '../lib/analytics'

// A tiny external store for the sky. The server snapshot is null, so SSR and
// hydration render nothing that depends on the roll; the first client read
// picks up what the head script stamped on <html>.

export { SCENES }

export const PHASE_LABELS = {
  dawn: 'dawn',
  day: 'daylight',
  golden: 'golden hour',
  dusk: 'dusk',
  night: 'night',
}

const listeners = new Set()
let snapshot = null
let started = false

function refresh() {
  const root = document.documentElement
  const sky = choosePhase(window.location.search, root, readPhase, readT, skyFor, dayAt)
  sky.scene = chooseScene(window.location.search, root, readScene)
  scatterClouds(root)
  snapshot = sky
  applySky(document, sky)
  listeners.forEach((l) => l())
}

// Once settled, the day runs: a clock moves the cycle position on at a pace
// that takes the sun from rising to setting in twenty minutes (a whole day
// and night in about thirty-three), and the phase changes as the sun crosses
// into each window. The sun's height is written five times a second, which
// on its own compositor layer is a sub-pixel glide; the landscape's sun-lit
// parts follow --sun-alt-slow, stepped every thirtieth of the arc with an
// eased transition, so the scene itself is repainted every twenty seconds
// rather than every frame. Time stands still while the tab is hidden.
const CYCLE_SECONDS = 2000
const TICK_MS = 200
const SLOW_STEP = 1 / 30
let driftTimer = null
let clock = null
let clockAt = 0
let slowAlt = NaN
function stopDrift() {
  clearTimeout(driftTimer)
  clearInterval(clock)
  clock = null
}
function stamp(root, day) {
  root.setAttribute('data-phase', day.phase)
  root.setAttribute('data-sun-alt', day.sunAlt.toFixed(3))
  root.setAttribute('data-day-t', day.t.toFixed(4))
}
function startDrift() {
  const root = document.documentElement
  let t = parseFloat(root.getAttribute('data-day-t'))
  // With transitions off the crawl would be a series of jumps, so it does not start.
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (isNaN(t) || reduce) return
  root.style.setProperty('--sun-drift', '0s')
  clockAt = performance.now()
  slowAlt = parseFloat(root.style.getPropertyValue('--sun-alt-slow'))
  clearInterval(clock)
  clock = setInterval(() => {
    const now = performance.now()
    // A tick after a hidden spell counts at most one tick of time.
    const dt = Math.min(now - clockAt, TICK_MS * 2) / 1000
    clockAt = now
    if (document.hidden) return
    t = (t + dt / CYCLE_SECONDS) % 1
    const day = dayAt(t)
    root.style.setProperty('--sun-alt', day.sunAlt.toFixed(3))
    if (isNaN(slowAlt) || Math.abs(day.sunAlt - slowAlt) >= SLOW_STEP) {
      slowAlt = day.sunAlt
      root.style.setProperty('--sun-alt-slow', slowAlt.toFixed(3))
    }
    const phaseChanged = snapshot && day.phase !== snapshot.phase
    stamp(root, day)
    if (snapshot) { snapshot.sunAlt = day.sunAlt; snapshot.t = day.t }
    if (phaseChanged) refresh()
  }, TICK_MS)
}

// The sun is drawn at its height from first paint and stays put until the
// clock begins.
function start() {
  if (started || typeof window === 'undefined') return
  started = true
  refresh()
  // Transitions are enabled only now, so the first paint never crossfades.
  document.documentElement.setAttribute('data-live', '')
  // A hidden spell does not count as time passing.
  document.addEventListener('visibilitychange', () => { clockAt = performance.now() })
  driftTimer = setTimeout(startDrift, 1800)
}

function subscribe(listener) {
  listeners.add(listener)
  start()
  return () => listeners.delete(listener)
}

const getSnapshot = () => snapshot
const getServerSnapshot = () => null

export function useSky() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

// A ?phase= or ?scene= query is for QA and would otherwise outrank the click.
function dropQuery(name) {
  try {
    const url = new URL(window.location.href)
    if (url.searchParams.has(name)) {
      url.searchParams.delete(name)
      window.history.replaceState(window.history.state, '', url)
    }
  } catch (e) { /* ignore */ }
}

// Anything that steps the hour (the sun itself, the masthead button) can
// register for the moment it happens, e.g. to play the sun's wobble.
const stepListeners = new Set()
export function onSunStep(fn) {
  stepListeners.add(fn)
  return () => stepListeners.delete(fn)
}

// A quick move takes this long; the clock resumes after it.
const SUN_MOVE_MS = 1000

// Jumps the day to a moment: the sun eases to its new height and the clock
// runs on from there.
function moveDayTo(t) {
  const root = document.documentElement
  stopDrift()
  root.style.setProperty('--sun-drift', '900ms')
  root.style.setProperty('--sun-ease', 'ease')
  stamp(root, dayAt(t))
  root.setAttribute('data-rolled', '1')
  driftTimer = setTimeout(startDrift, SUN_MOVE_MS)
}

// The sun steps the day on to a random moment in the next phase, anywhere
// in that phase's stretch of sky; nothing is saved. The sun in the sky and
// the masthead icon report as separate events, since GA4 lists event names
// in its reports but not parameters.
export function stepPhase(source = 'sun') {
  const root = document.documentElement
  const from = (snapshot && snapshot.phase) || root.getAttribute('data-phase') || 'golden'
  const next = nextPhase(from)
  track(source === 'masthead' ? 'sun_icon_click' : 'sun_click', { phase_from: from, phase_to: next, theme: root.getAttribute('data-scene') || 'bay' })
  dropQuery('phase')
  dropQuery('t')
  moveDayTo(skyFor(next, dayAt).t)
  refresh()
  stepListeners.forEach((fn) => fn())
}

// The picker changes the scene for this page load only; nothing is saved.
export function setScene(name) {
  if (!SCENES.includes(name)) return
  const root = document.documentElement
  const from = root.getAttribute('data-scene') || 'bay'
  if (from !== name) track('theme_change', { theme_from: from, theme_to: name, phase: root.getAttribute('data-phase') || 'golden' })
  dropQuery('scene')
  root.setAttribute('data-scene', name)
  // The landscape crossfades under a sun that stays exactly where it is.
  refresh()
}
